import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete01Icon } from '@hugeicons/core-free-icons'

import {
  useAdminUserDetail,
  useAdminDeleteUser,
  useAdminSetPlatformAdmin,
} from '@/features/admin/services/admin.convex'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute(
  '/(dashboard)/admin/users/$userId/settings/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    name: (search.name as string) || undefined,
  }),
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Admin' },
      { title: 'Users', href: '/admin/users' },
      { title: search.name || params.userId },
      { title: 'Settings' },
    ],
  }),
})

function RouteComponent() {
  const { userId } = Route.useParams()
  const navigate = useNavigate()
  const { showConfirmDialog } = useConfirmDialog()

  const { data: user } = useAdminUserDetail(userId)
  const deleteUser = useAdminDeleteUser()
  const setPlatformAdmin = useAdminSetPlatformAdmin()

  if (!user) return null

  const isInPlatformOrg = user.organizations.some((o) => o?.isAdmin)

  const handleToggleAdmin = async () => {
    try {
      await setPlatformAdmin({
        userId: userId as never,
        isPlatformAdmin: !user.isPlatformAdmin,
      })
      toast.success(
        user.isPlatformAdmin
          ? 'Platform admin revoked'
          : 'Platform admin granted',
      )
    } catch {
      toast.error('Failed to update admin status')
    }
  }

  const handleDelete = () => {
    const label = user.name ?? user.email ?? user.id
    showConfirmDialog({
      title: 'Delete User',
      description: `This will permanently delete "${label}" and all their data. This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'destructive',
      requireInput: { match: label },
      onConfirm: async () => {
        try {
          await deleteUser({ userId: userId as never })
          toast.success(`User "${label}" deleted`)
          navigate({ to: '/admin/users' })
        } catch {
          toast.error('Failed to delete user')
        }
      },
    })
  }

  return (
    <div className="space-y-6">
      {/* Platform admin toggle — only shown for platform org members */}
      {isInPlatformOrg ? (
        <div className="flex items-center gap-3 p-6 bg-muted/30 rounded-lg">
          <Switch
            id="platform-admin"
            checked={user.isPlatformAdmin}
            onCheckedChange={handleToggleAdmin}
          />
          <Label htmlFor="platform-admin" className="cursor-pointer">
            <span className="font-medium">Platform Admin</span>
            <p className="text-xs text-muted-foreground">
              Grants access to the /admin panel and all platform-level data.
            </p>
          </Label>
        </div>
      ) : null}

      {/* Danger Zone */}
      <div className="p-6 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 flex items-center justify-between gap-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Danger Zone
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Permanently delete this user and all their data from the platform.
            This action is irreversible.
          </p>
        </div>
        <Button
          variant="destructive"
          onClick={handleDelete}
          className="shrink-0"
        >
          <HugeiconsIcon icon={Delete01Icon} className="mr-2 h-4 w-4" />
          Delete User
        </Button>
      </div>
    </div>
  )
}
